import axiosHttp from 'axios';
import { useIframeCommunication } from 'contexts/IframeMessageListener';
import { initLogin } from 'pages/auth/hooks/useAuthSass/utils';
import { useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
  selectMasterSlaveIframe,
  updateMasterSlaveFromHowAmI
} from '../../../common/Components/MasterSlave/controllers/actions';
import { RootState, useAppDispatch } from '../../../store';

import { selectSiteData } from 'store/slices/site';
import { isUserLoaded, setLoading } from 'store/slices/userSlice';
import { postIframeMessage } from 'utils/postPerentIframeMessage';
import { SelectSitePayload } from './site.types';
import { MAX_RETRIES, SESSION_LOGGING_MESSAGE_KEY } from 'pages/SiteLoadingPage/constants';

export default function useIframeSiteSelection() {
  const dispatch = useAppDispatch();

  const { currentSelectedSite, site } = useSelector(selectSiteData);
  const { connection } = useIframeCommunication();

  const { isGlobalMode } = useSelector((state: RootState) => state.user.loggedUser);

  const CurrentSiteGuid = useSelector((state: RootState) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (state.masterSlave as any)?.masterSlave?.CurrentSiteGuid;
  });
  const userLoaded = useSelector(isUserLoaded);

  const isFirstSiteSelectRef = useRef(true);

  const sendLoadedMessage = useCallback(() => {
    if (isFirstSiteSelectRef.current) {
      postIframeMessage(SESSION_LOGGING_MESSAGE_KEY, {
        step: 'useIframeSiteSelection:onSelectSite:sendLoadedMessage'
      });
      postIframeMessage('onIframeLoaded');
      isFirstSiteSelectRef.current = false;
    }
  }, []);

  const sendMasterSlaveSelectedMessage = useCallback(async () => {
    try {
      postIframeMessage(SESSION_LOGGING_MESSAGE_KEY, {
        step: 'useIframeSiteSelection:onSelectSite:sendMasterSlaveSelectedMessage'
      });
      await connection?.query.call('setIsMasterSlaveApplied', true);
    } catch (error) {
      postIframeMessage(SESSION_LOGGING_MESSAGE_KEY, {
        type: 'error',
        step: 'useIframeSiteSelection:onSelectSite:sendMasterSlaveSelectedMessage',
        details: {
          error: JSON.stringify(error),
          message: 'Failed to send setIsMasterSlaveApplied event with Coriolis!'
        }
      });

      throw error;
    }
  }, [connection?.query]);

  const onSelectSite = useCallback(
    async ({ site, currentSelectedSite }: SelectSitePayload) => {
      let attempt = 1;

      while (attempt <= MAX_RETRIES) {
        try {
          postIframeMessage(SESSION_LOGGING_MESSAGE_KEY, {
            step: 'useIframeSiteSelection:onSelectSite:start',
            details: {
              site: currentSelectedSite?.DbTenantId,
              currentSelectedSite: currentSelectedSite?.DbTenantId,
              currentIframeSiteId: CurrentSiteGuid,
              isFirstSiteSelect: isFirstSiteSelectRef.current,
              attempt
            }
          });

          if ((site?.DbTenantId ?? null) !== CurrentSiteGuid) {
            let skipSelectMasterSlave = false;
            // TODO: legacy support, isMaster should be deleted in the future, 'selectMasterSlave' action should be renamed
            let isMaster = false;
            dispatch(setLoading(true));

            if (!site) {
              // when site is null, it means we are in "All" site
              postIframeMessage(SESSION_LOGGING_MESSAGE_KEY, {
                step: 'useIframeSiteSelection:onSelectSite:initLogin_AllSite'
              });
              await initLogin(axiosHttp);
              isMaster = true;
              skipSelectMasterSlave = true;
            } else if (site.userGuid !== currentSelectedSite?.userGuid) {
              // same global and different user
              postIframeMessage(SESSION_LOGGING_MESSAGE_KEY, {
                step: 'useIframeSiteSelection:onSelectSite:initLogin_SwitchUser',
                details: { userGuid: site.userGuid }
              });
              await initLogin(axiosHttp, site.userGuid);
            }

            postIframeMessage(SESSION_LOGGING_MESSAGE_KEY, {
              step: 'useIframeSiteSelection:onSelectSite:dispatchSelectMasterSlave',
              details: { isMaster, isGlobalMode, skipSelectMasterSlave, site }
            });
            await dispatch(selectMasterSlaveIframe(isMaster, site, isGlobalMode, skipSelectMasterSlave));
          }

          postIframeMessage(SESSION_LOGGING_MESSAGE_KEY, {
            step: 'useIframeSiteSelection:onSelectSite:startSendingLoadedMessages',
            details: { siteId: site?.DbTenantId }
          });
          dispatch(updateMasterSlaveFromHowAmI(site));
          sendLoadedMessage();
          await sendMasterSlaveSelectedMessage();
          postIframeMessage(SESSION_LOGGING_MESSAGE_KEY, {
            step: 'useIframeSiteSelection:onSelectSite:finishSendingLoadedMessages',
            details: { siteId: site?.DbTenantId }
          });
          return;
        } catch (error) {
          if (attempt === MAX_RETRIES) {
            postIframeMessage(SESSION_LOGGING_MESSAGE_KEY, {
              type: 'error',
              step: 'useIframeSiteSelection:onSelectSite:CATCH_BLOCK_ERROR',
              rumMeasurementName: 'Iframe Session Initialization Error',
              details: {
                error: JSON.stringify(error),
                message: `Failed to select site, max retry count (${MAX_RETRIES}) exceeded !`,
                siteId: site?.DbTenantId
              }
            });
            return;
          }

          postIframeMessage(SESSION_LOGGING_MESSAGE_KEY, {
            step: 'useIframeSiteSelection:onSelectSite:retry',
            details: { attempt, error: JSON.stringify(error), siteId: site?.DbTenantId }
          });
          const retryDelay = 100 * attempt;
          await new Promise(resolve => setTimeout(resolve, retryDelay));
          attempt++;
        }
      }
    },
    [CurrentSiteGuid, dispatch, isGlobalMode, sendLoadedMessage, sendMasterSlaveSelectedMessage]
  );

  useEffect(() => {
    // Wait for user data to be loaded before selecting site to prevent race conditions
    // when old CyFront is rendered as iframe in new CyFront

    postIframeMessage(SESSION_LOGGING_MESSAGE_KEY, {
      step: 'useIframeSiteSelection:useEffect:start',
      details: { userLoaded, hasCurrentSelectedSite: currentSelectedSite !== undefined }
    });

    if (userLoaded && currentSelectedSite !== undefined) {
      postIframeMessage(SESSION_LOGGING_MESSAGE_KEY, {
        step: 'useIframeSiteSelection:useEffect:triggerOnSelectSite',
        details: { userLoaded, currentSelectedSiteId: currentSelectedSite?.DbTenantId }
      });
      onSelectSite({ site, currentSelectedSite });
    }
  }, [currentSelectedSite, site, userLoaded, onSelectSite]);
}
